import _ from 'lodash';
import _find from 'lodash/find';
import _keyBy from 'lodash/keyBy';
import React, { useRef } from 'react';
import numeral from 'numeral';
import classnames from 'classnames';
import pluralize from 'pluralize';
import { Link } from 'react-router-dom';
import { reverse } from 'named-urls';
import MediaQuery from 'react-responsive';
import routes from '@achievable-me/tundra-wizard/routes';
import { canAuthor } from '@achievable-me/tundra-wizard/auth';
import ProductLayout from '$components/layout/ProductLayout';
import Button from '$components/library/Button';
import ScrollToTop from '$components/library/ScrollToTop';
import { checkAccess } from '$lib/access';
import CompleteContentButton from './CompleteContentButton';
import NextContentButton from './NextContentButton';
import PrevContentButton from './PrevContentButton';
import ProductContentNotFound from './ProductContentNotFound';
import ProductUpgrade from '$components/student/ProductUpgrade';
import SignUpComponent from './SignUpComponent';
import ContentViewer from '$components/student/ContentViewer';
import { isBot } from '$lib/userAgent';
import SeoBreadcrumbs from './SeoBreadcrumbs';
import ShareLink from './ShareLink';
import styles from './styles.scss';
import { useTrack } from '$lib/tracking';

const EARLY_ACCESS_SLUGS = ['amc-8', 'amc-10', 'amc-12'];

const PrivateBrowsing = () => (
  <div data-aaa="privateBrowsing" className={styles.privateBrowsing}>
    <h2 className={styles.privateBrowsingHeader}>Private browsing</h2>
    <i className="fad fa-ghost" />
    <div className={styles.privateBrowsingMessage}>
      <p>Hi friend,</p>
      <p>It looks like you&apos;re using a private browsing mode.</p>
      <p>
        As a company policy, we respect your right to privacy, and so we collect and store
        as little information as we need to run the site. We&apos;d love to be able to
        offer everything for free to everyone, but then we wouldn&apos;t be able to afford
        producing and maintaining our content. Using a content metering approach means
        that we can reach a compromise - offering casual users looking for tidbits of info
        a few pages for free, while paying our bills with the revenue from serious
        studiers who purchase our full program. In order to do this, we need to be able to
        track you in some minimal way, so we have to disallow access in private browsing
        mode.
      </p>
      <p>Thanks for understanding,</p>
      <p>The Achievable Team</p>
    </div>
  </div>
);

const CompleteToUnlock = ({ content, currentUser, productSlug }) => {
  const { completedAt, numItems } = content;
  if (completedAt) {
    if (numItems) {
      return (
        <p className={styles.completeToUnlock}>
          Access quizzes for this chapter via your dashboard
        </p>
      );
    }

    return <p className={styles.completeToUnlock}>&nbsp;</p>;
  }

  if (currentUser) {
    if (numItems) {
      const quizPlural =
        content.numItems === 1 ? '1 review quiz' : `${content.numItems} review quizzes`;

      return (
        <p className={styles.completeToUnlock}>
          &quot;<b>Complete</b>&quot; this chapter to unlock {quizPlural}
        </p>
      );
    }

    if (_.includes(EARLY_ACCESS_SLUGS, productSlug)) {
      return (
        <p className={styles.completeToUnlock}>
          This content is still a work in progress; check back later to
          &quot;complete&quot; it
        </p>
      );
    }

    return (
      <p className={styles.completeToUnlock}>
        &quot;<b>Complete</b>&quot; this chapter to fill in your progress chart
      </p>
    );
  }

  return null;
};

const CommunityLink = () => {
  const href = 'https://talk.achievable.me/'; // 20200506JP: Have this go to the exam category once we have posts.
  return (
    <div className={styles.community}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        <i className={classnames('fad fa-comments', styles.bannerIcon)} /> Discussion{' '}
        <i className="fad fa-external-link" />
      </a>
    </div>
  );
};

const ContentMeter = ({ canView, contentRemaining, isMetered }) => {
  if (!isMetered) {
    return null;
  }

  let meterContent = null;

  if (!canView) {
    meterContent = (
      <>
        <i className={classnames('fad fa-exclamation-circle warn', styles.bannerIcon)} />
        You&apos;re out of free content!
      </>
    );
  } else if (contentRemaining === 0) {
    meterContent = (
      <>
        <i className={classnames('fad fa-exclamation-circle warn', styles.bannerIcon)} />
        You&apos;re out of free content!
      </>
    );
  } else {
    meterContent = (
      <>
        <i className={classnames('fad fa-exclamation-circle warn', styles.bannerIcon)} />{' '}
        You can read {pluralize('more page', contentRemaining, true)} for free
      </>
    );
  }

  return (
    <div className={styles.banner}>
      <div data-aaa="bannerMeter" className={styles.bannerMeter}>
        {meterContent}
      </div>
    </div>
  );
};

const SignUpCta = () => (
  <div className={styles.banner}>
    <div data-aaa="bannerSignUp" className={styles.bannerSignUpCta}>
      <div className={styles.bannerSignUpCtaText}>
        Want to take quizzes and track your progress?
      </div>
      <div>
        <Link to={routes.auth.signUp}>
          <Button data-aaa="signUpButton">Sign up for free</Button>
        </Link>
      </div>
    </div>
  </div>
);

const SignUpForm = ({ content, product }) => {
  const { uuid: contentUuid } = content;
  const { slug: productSlug } = product;
  useTrack('Topic | Paywalled', {
    productSlug,
    contentUuid,
  });
  return (
    <div className={classnames(styles.banner, styles.bannerSignUpForm)}>
      <div className={styles.bannerSignUpFormText}>
        Read more now by creating a free account
      </div>
      <div className={styles.bannerSignUpFormSubtext}>
        Plus, take quizzes and track your progress
      </div>
      <SignUpComponent product={product} />
    </div>
  );
};

const UpgradeCta = ({ product }) => {
  const { isPassFail, price, shortName, slug: productSlug } = product;
  const priceF = numeral(price).format('$0,0');

  const ctaLead = isPassFail ? 'Pass the first time' : 'Reach your target score';

  return (
    <div className={styles.banner}>
      <div data-aaa="bannerUpgrade" className={styles.bannerSignUpCta}>
        <div className={styles.bannerSignUpCtaText}>
          {ctaLead} with {shortName}
        </div>
        <div>
          <Link to={reverse(routes.study.purchase, { productSlug })}>
            <Button data-aaa="upgradeButton">Buy the full program - {priceF}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const PurchaseForm = ({ currentUser, product }) => (
  <div className={styles.banner}>
    <div className={styles.bannerProductUpgradeForm}>
      <ProductUpgrade currentUser={currentUser} product={product} />
    </div>
  </div>
);

const EarlyAccess = ({ product }) => {
  const { slug } = product;

  if (slug === 'amc-8') {
    return (
      <div className={classnames(styles.banner, styles.bannerEarlyAccess)}>
        <div>
          Our AMC 8 course is in &quot;early access&quot;; the content on this page may be
          incomplete.
        </div>
      </div>
    );
  }

  return null;
};

const ChapterHeader = ({ contentInstance, flatContent, product }) => {
  const foundInstance = _find(flatContent, (o) => o.uuid === contentInstance.uuid);
  const chapterPrefix = `${product.shortName} Ch ${foundInstance.titlePrefix}`;

  const parentsEl = (() => {
    if (!foundInstance.parentUuid) {
      return null;
    }

    const parent = _find(flatContent, (o) => o.uuid === foundInstance.parentUuid);

    const grandparentEl = (() => {
      if (!parent.parentUuid) {
        return null;
      }

      const grandparent = _find(flatContent, (o) => o.uuid === parent.parentUuid);
      return <div className={styles.headerGrandparent}> {grandparent.name} </div>;
    })();

    return (
      <>
        {grandparentEl}
        <div className={styles.headerParent}>{parent.name}</div>
      </>
    );
  })();

  return (
    <>
      <div className={styles.headerChapter}>{chapterPrefix}</div>
      {parentsEl}
    </>
  );
};

const ProductContent = (props) => {
  const layoutRef = useRef();

  const { currentUser, location, match } = props;
  const { params } = match;
  const { contentUuid } = params;
  const isBotResult = isBot();

  // 20190930JP: <Seo/> is handled in <ContentViewer/>

  return (
    <ProductLayout currentUser={currentUser} location={location} match={match}>
      {({ flatContent, product }) => {
        const { slug: productSlug } = product;
        const isAchievable = productSlug === 'achievable';
        const contentByUuid = _keyBy(product.content, 'uuid');
        const contentBySlug = _keyBy(product.content, 'slug');
        const content = contentByUuid[contentUuid] || contentBySlug[contentUuid];

        if (!content) {
          return <ProductContentNotFound product={product} contentUuid={contentUuid} />;
        }

        const { name, readingTimeMs } = content;

        const authorEls = (() => {
          if (!canAuthor({ user: currentUser, product })) {
            return null;
          }

          const editTo = reverse(routes.staff.product.content.edit, {
            productSlug,
            contentUuid: content.uuid,
          });

          return (
            <>
              <Link target="_blank" to={editTo}>
                <i className="fad fa-edit" />
              </Link>
              <div className={styles.divider}>|</div>
            </>
          );
        })();

        const { canView, contentRemaining, isMetered } = checkAccess({
          content,
          product,
          user: currentUser,
        });
        const shouldRenderMeter = !isBotResult;
        const shouldRenderSignUpCta =
          !isBotResult && !isAchievable && !currentUser && canView;
        const shouldRenderSignUpForm = !isBotResult && !currentUser && !canView;
        const shouldRenderUpgradeForm = !isBotResult && currentUser && !canView;
        const shouldRenderUpgradeCta =
          !isBotResult && !isAchievable && currentUser && canView && isMetered;

        const readingTime = `${Math.max(
          1,
          Math.ceil(readingTimeMs / 1000 / 60)
        )} min read`;

        return (
          <ScrollToTop elementRef={layoutRef}>
            <div ref={layoutRef} className={styles.layout}>
              <div className={styles.contentHeader}>
                <div className={styles.contentHeaderPre}>
                  <SeoBreadcrumbs contentInstance={content} product={product} />
                  <ChapterHeader
                    contentInstance={content}
                    flatContent={flatContent}
                    product={product}
                  />
                  <h1 className={styles.header}>{name}</h1>
                </div>
              </div>
              <div className={styles.contentWrapper}>
                {window.Aaa.isPrivateBrowsing && !currentUser ? (
                  <div className={styles.contentWrapperTranslate}>
                    <div className={styles.contentWrapperMain}>
                      <div className={styles.content}>
                        <PrivateBrowsing />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.contentWrapperTranslate}>
                    {shouldRenderMeter && (
                      <ContentMeter
                        canView={canView}
                        contentRemaining={contentRemaining}
                        isMetered={isMetered}
                      />
                    )}
                    <EarlyAccess product={product} />
                    {shouldRenderSignUpCta && <SignUpCta />}
                    {shouldRenderSignUpForm && (
                      <SignUpForm content={content} product={product} />
                    )}
                    {shouldRenderUpgradeForm && (
                      <PurchaseForm currentUser={currentUser} product={product} />
                    )}
                    {canView && (
                      <div
                        data-aaa="productContent"
                        className={styles.contentWrapperMain}
                      >
                        <div className={styles.content}>
                          <div className={styles.miniHeader}>
                            {authorEls}
                            <div className={styles.readingTime}>{readingTime}</div>
                            <div className={styles.divider}>|</div>
                            <CommunityLink />
                            <div className={styles.divider}>|</div>
                            <ShareLink product={product} />
                          </div>
                          <ContentViewer contentInstance={content} product={product} />
                          {!isMetered && product.slug !== 'achievable' && (
                            <CompleteToUnlock
                              content={content}
                              currentUser={currentUser}
                              productSlug={productSlug}
                            />
                          )}
                          <div className={styles.actions}>
                            <PrevContentButton
                              contentInstance={content}
                              flatContent={flatContent}
                            />
                            {!isAchievable && currentUser && !isMetered && (
                              <MediaQuery maxDeviceWidth={575}>
                                <CompleteContentButton
                                  contentInstance={content}
                                  productSlug={productSlug}
                                />
                              </MediaQuery>
                            )}
                            <div className={styles.rightActions}>
                              {!isAchievable && currentUser && !isMetered && (
                                <MediaQuery minDeviceWidth={576}>
                                  <CompleteContentButton
                                    contentInstance={content}
                                    productSlug={productSlug}
                                    className={styles.rightCompleteButton}
                                  />
                                </MediaQuery>
                              )}
                              <NextContentButton
                                contentInstance={content}
                                currentUser={currentUser}
                                flatContent={flatContent}
                                isAchievable={isAchievable}
                                product={product}
                              />
                            </div>
                          </div>
                        </div>
                        {shouldRenderUpgradeCta && <UpgradeCta product={product} />}
                      </div>
                    )}
                    {shouldRenderSignUpCta && <SignUpCta />}
                  </div>
                )}
              </div>
              <div className={styles.copyright}>
                All rights reserved &copy;2016 - {new Date().getFullYear()} Achievable,
                Inc.
              </div>
            </div>
          </ScrollToTop>
        );
      }}
    </ProductLayout>
  );
};

export default ProductContent;
